import { Link } from "react-router-dom";
import { ArrowRight, Landmark, Scale, Building2, Users } from "lucide-react";
import { Reveal, MaskedLine, Eyebrow, SectionHead } from "../components/Motion";

const DC_IMG =
  "https://images.pexels.com/photos/14844426/pexels-photo-14844426.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const TIMELINE = [
  {
    year: "2014",
    title: "Chartered by Congress",
    body: "Established as a pilot program within the national defense authorization to confront a widening federal cyber skills gap.",
  },
  {
    year: "2017",
    title: "National Cyber Range Opens",
    body: "Facility 07 begins live-fire training on a full-scale replica of federal enterprise and cloud terrain.",
  },
  {
    year: "2019",
    title: "Executive Order 14028 Alignment",
    body: "Curriculum remapped end-to-end to the national cybersecurity executive order and NIST framework families.",
  },
  {
    year: "2022",
    title: "CMMC Training Mandate",
    body: "Designated a certified training provider for the Cybersecurity Maturity Model Certification workforce.",
  },
  {
    year: "2024",
    title: "ICS Range Expansion",
    body: "A dedicated industrial range opens for energy, water and transportation sector defenders.",
  },
  {
    year: "2026",
    title: "14,500 Operators Strong",
    body: "Alumni now serve across federal agencies, the armed forces and critical infrastructure sectors nationwide.",
  },
];

const DIRECTIVES = [
  {
    icon: Landmark,
    name: "Executive Order 14028",
    sub: "Improving the Nation's Cybersecurity",
    points: ["Zero trust migration curriculum", "Software supply chain modules", "Incident reporting doctrine"],
  },
  {
    icon: Scale,
    name: "NIST SP 800-53 Rev. 5",
    sub: "Security & Privacy Controls",
    points: ["Control-family mapped syllabi", "Assessment & authorization labs", "Privacy engineering tracks"],
  },
  {
    icon: Building2,
    name: "CMMC 2.0",
    sub: "Cybersecurity Maturity Model Certification",
    points: ["Certified training provider", "Level 1–3 readiness programs", "Assessor preparation courses"],
  },
  {
    icon: Users,
    name: "FISMA",
    sub: "Federal Information Security Modernization",
    points: ["Agency reporting practica", "Risk management framework labs", "Continuous monitoring training"],
  },
];

const LEADERSHIP = [
  { initials: "EC", name: "Rear Adm. (Ret.) E. Castellanos", role: "Superintendent" },
  { initials: "MO", name: "Dr. M. Okafor", role: "Dean of Cyber Operations" },
  { initials: "HL", name: "H. Lindqvist", role: "Director, National Cyber Range" },
  { initials: "JM", name: "Col. (Ret.) J. Mercer", role: "Chair, Governance Board" },
];

const About = () => (
  <div data-testid="about-page">
    {/* Header band */}
    <section className="relative bg-navy-950 grid-lines overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-44 pb-20 lg:pt-52 lg:pb-24">
        <MaskedLine as="div" delay={0.3} className="mb-6">
          <Eyebrow light>About the Academy</Eyebrow>
        </MaskedLine>
        <h1 className="font-serif font-bold tracking-tight leading-[1.08] text-4xl sm:text-5xl lg:text-6xl text-white max-w-3xl" data-testid="about-headline">
          <MaskedLine delay={0.45}>A national mandate,</MaskedLine>
          <MaskedLine delay={0.57}>
            <span>kept since <em className="text-gold-300 italic">2014</em>.</span>
          </MaskedLine>
        </h1>
        <MaskedLine delay={0.7} className="mt-8 max-w-2xl">
          <p className="text-base sm:text-lg leading-relaxed text-slate-300">
            The United States' federal agencies exist to serve and protect the
            public — defending consumers, enforcing the law, safeguarding benefits
            and securing the nation. This directory brings their work together.
          </p>
        </MaskedLine>
      </div>
    </section>

    {/* Mandate */}
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28" data-testid="mandate-section">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <div>
          <SectionHead
            eyebrow="Institutional Mandate"
            title="Education as an instrument of national defense."
            lede="The Academy operates under a simple conviction: technology alone does not secure the nation — trained people do. Our campuses, ranges and virtual classrooms exist to produce those people at national scale."
          />
          <Reveal delay={0.2}>
            <div className="mt-8 space-y-5 text-sm sm:text-base leading-relaxed text-slate-600">
              <p>
                From six-week foundations to executive wargames, every program is
                built with the same discipline: doctrine first, hands always,
                assessment without exception. Graduates leave with evidence of
                capability — not just certificates of attendance.
              </p>
              <p>
                Instruction is delivered across secure in-person campuses, a
                virtual academy, and the National Cyber Range, where staged
                adversaries behave like real ones.
              </p>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          <div className="federal-frame relative overflow-hidden bg-navy-900">
            <img
              src={DC_IMG}
              alt="Federal architecture in Washington, D.C."
              loading="lazy"
              className="w-full h-[420px] lg:h-[520px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy-950/60 to-transparent" />
            <div className="absolute bottom-6 left-6 z-10 chip border-gold-500/60 text-gold-200 bg-navy-950/70 backdrop-blur-sm">
              Washington, D.C. · Headquarters
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    {/* Timeline */}
    <section className="bg-navy-950 border-y border-gold-500/20" data-testid="timeline-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <SectionHead
          light
          eyebrow="Institutional History"
          title="Twelve years of national service."
        />
        <div className="mt-16 relative">
          <div className="absolute left-[7px] lg:left-1/2 top-0 bottom-0 w-px bg-gold-500/25" />
          <div className="space-y-12">
            {TIMELINE.map((t, i) => (
              <Reveal key={t.year} delay={0.05 * i}>
                <div className={`relative flex ${i % 2 === 0 ? "lg:justify-start" : "lg:justify-end"}`}>
                  <div className={`pl-10 lg:pl-0 lg:w-1/2 ${i % 2 === 0 ? "lg:pr-14" : "lg:pl-14"}`}>
                    <div className="bg-navy-900 border border-gold-500/20 p-7 transition-all duration-300 hover:border-gold-500/50">
                      <span className="font-mono text-gold-400 text-sm tracking-[0.2em]">{t.year}</span>
                      <h3 className="mt-2 font-serif font-semibold text-xl text-white">{t.title}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-slate-400">{t.body}</p>
                    </div>
                  </div>
                  <span className="absolute left-0 lg:left-1/2 lg:-translate-x-1/2 top-7 w-4 h-4 rotate-45 bg-gold-500 border-4 border-navy-950" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* Directive matrix */}
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28" data-testid="directives-section">
      <SectionHead
        eyebrow="Compliance Alignment"
        title="Mapped to the national framework, line by line."
        lede="Every syllabus is audited against the directives that govern federal systems — so your workforce trains to the standard it will be assessed against."
      />
      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
        {DIRECTIVES.map((d, i) => (
          <Reveal key={d.name} delay={0.06 * i} className="h-full">
            <div className="group h-full bg-white border border-paper-border p-8 transition-all duration-300 hover:border-gold-500/60 hover:-translate-y-1 hover:shadow-xl hover:shadow-gold-500/10">
              <div className="flex items-start justify-between gap-4">
                <d.icon className="w-7 h-7 text-gold-600" strokeWidth={1.5} />
                <span className="chip border-cleargreen/30 text-cleargreen">Aligned</span>
              </div>
              <h3 className="mt-5 font-serif font-semibold text-xl text-navy-900">{d.name}</h3>
              <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-slate-500 mt-1">{d.sub}</p>
              <ul className="mt-5 space-y-2.5">
                {d.points.map((p) => (
                  <li key={p} className="text-sm text-slate-600 flex gap-2.5">
                    <span className="w-1 h-1 rounded-full bg-gold-500 mt-2 shrink-0" /> {p}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    {/* Leadership */}
    <section className="bg-paper-100 border-t border-paper-border" data-testid="leadership-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
        <SectionHead
          eyebrow="Senior Leadership"
          title="Governed by operators, engineers and public servants."
        />
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {LEADERSHIP.map((p, i) => (
            <Reveal key={p.name} delay={0.06 * i} className="h-full">
              <div className="h-full bg-white border border-paper-border p-8 text-center transition-all duration-300 hover:border-gold-500/60 hover:-translate-y-1">
                <div className="w-16 h-16 mx-auto rounded-full bg-navy-900 border-2 border-gold-500/60 flex items-center justify-center font-serif font-bold text-xl text-gold-300">
                  {p.initials}
                </div>
                <h3 className="mt-5 font-serif font-semibold text-lg text-navy-900 leading-snug">{p.name}</h3>
                <p className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-gold-600">{p.role}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <div className="mt-12 flex justify-center">
            <Link
              to="/contact"
              data-testid="leadership-contact-cta"
              className="group inline-flex items-center gap-2 h-12 px-7 rounded-full bg-navy-900 text-gold-300 text-sm font-semibold tracking-wide transition-all duration-200 hover:bg-navy-800 hover:-translate-y-0.5 hover:shadow-lg"
            >
              Engage the Governance Board <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  </div>
);

export default About;
